/**
 * Componente compartilhado das 4 telas de tarifas bancárias.
 *
 * Um recurso único no backend atende as 4 telas do protótipo:
 *   GET /finance/bank-tariffs?channel=GENERAL|CARD|PIX|TRANSFER
 *     → PaginatedResponseDto<BankTariffResponseDto>
 *   POST /finance/bank-tariffs         (BankTariffInputDto)
 *   PUT  /finance/bank-tariffs/:id     (BankTariffUpdateInputDto)
 *   DELETE /finance/bank-tariffs/:id   → { id, deleted }
 *
 * treasury.dto.ts — BankTariffResponseDto:
 *   id, service, channel, modality?, brand?, chargeType (FIXED|PERCENTAGE),
 *   currentValue, isActive, createdAt, updatedAt
 *
 * A tela de CARTÕES (protótipo erpone_tarifas_banc_rias_cart_es) agrupa por
 * "Modalidade / Bandeira"; as demais mostram "Serviço / Evento". O valor é
 * R$ quando chargeType=FIXED e % quando PERCENTAGE — derivado do dado, não
 * de suposição de tela.
 */
import { useState } from 'react';
import {
  Card,
  CardHeader,
  Badge,
  Button,
  LoadingSkeleton,
  Modal,
  Input,
  Select,
} from '@nexora/design-system';
import {
  useBankTariffs,
  useCreateBankTariff,
  useUpdateBankTariff,
  useDeleteBankTariff,
} from './treasuryHooks';
import {
  CHARGE_TYPE_LABEL,
  type BankTariff,
  type BankTariffChannel,
  type ChargeType,
} from './treasuryTypes';
import { formatCurrency, formatNumber } from '../../../shared/utils/format';

/* ── Formato do valor conforme o tipo de cobrança ── */

function formatTariffValue(t: Pick<BankTariff, 'chargeType' | 'currentValue'>): string {
  if (t.chargeType === 'PERCENTAGE') {
    return `${formatNumber(t.currentValue, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
  }
  return formatCurrency(t.currentValue);
}

interface BankTariffsChannelPageProps {
  channel: BankTariffChannel;
  title: string;
  subtitle: string;
  /** CARTÕES mostra Modalidade + Bandeira como primeira coluna. */
  showModalityBrand?: boolean;
}

interface FormState {
  service: string;
  modality: string;
  brand: string;
  chargeType: ChargeType;
  currentValue: string;
}

const emptyForm: FormState = { service: '', modality: '', brand: '', chargeType: 'FIXED', currentValue: '' };

export function BankTariffsChannelPage({
  channel,
  title,
  subtitle,
  showModalityBrand = false,
}: BankTariffsChannelPageProps): JSX.Element {
  const { data, isLoading, error, refetch } = useBankTariffs(channel);
  const createMutation = useCreateBankTariff();
  const updateMutation = useUpdateBankTariff();
  const deleteMutation = useDeleteBankTariff();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<BankTariff | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<BankTariff | null>(null);
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);

  const tariffs = data?.data ?? [];
  const total = data?.total ?? 0;

  const showToast = (message: string, variant: 'success' | 'error'): void => {
    setToast({ message, variant });
    setTimeout(() => setToast(null), 4000);
  };

  const openCreate = (): void => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (t: BankTariff): void => {
    setEditing(t);
    setForm({
      service: t.service,
      modality: t.modality ?? '',
      brand: t.brand ?? '',
      chargeType: t.chargeType,
      currentValue: String(t.currentValue),
    });
    setModalOpen(true);
  };

  const handleSave = async (): Promise<void> => {
    if (!form.service || form.currentValue === '') {
      showToast('Preencha o serviço/evento e o valor atual.', 'error');
      return;
    }
    const payload = {
      service: form.service,
      channel,
      ...(showModalityBrand ? { modality: form.modality, brand: form.brand } : {}),
      chargeType: form.chargeType,
      currentValue: Number(form.currentValue),
    };
    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, ...payload });
        showToast('Tarifa atualizada com sucesso.', 'success');
      } else {
        await createMutation.mutateAsync(payload);
        showToast('Tarifa criada com sucesso.', 'success');
      }
      setModalOpen(false);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Erro ao salvar tarifa.', 'error');
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      showToast('Tarifa excluída.', 'success');
      setDeleteTarget(null);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Erro ao excluir tarifa.', 'error');
    }
  };

  return (
    <div className="flex-1 overflow-auto bg-surface-bright p-md md:p-lg">
      {/* ── Page header ── */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">{title}</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">{subtitle}</p>
        </div>
        <Button onClick={openCreate}>+ Nova Tarifa</Button>
      </div>

      {/* ── Loading ── */}
      {isLoading && (
        <Card padding="2rem">
          <LoadingSkeleton count={4} height="48px" />
          <p style={{ color: '#43474e', fontSize: '0.875rem' }}>Carregando tarifas...</p>
        </Card>
      )}

      {/* ── Error ── */}
      {error && !isLoading && (
        <Card padding="2rem" className="text-centered">
          <p style={{ color: '#ba1a1a', fontWeight: 600, margin: 0 }}>Erro ao carregar tarifas</p>
          <p style={{ fontSize: '0.875rem', color: '#74777f' }}>{(error as Error).message}</p>
          <Button size="sm" variant="outline" onClick={() => refetch()}>Tentar novamente</Button>
        </Card>
      )}

      {/* ── Empty honesto ── */}
      {!isLoading && !error && tariffs.length === 0 && (
        <Card padding="2rem" className="text-centered muted">
          <p style={{ margin: 0, fontSize: '0.875rem' }}>
            Nenhuma tarifa cadastrada para o canal {channel}.
          </p>
          <Button size="sm" variant="outline" onClick={openCreate} style={{ marginTop: '1rem' }}>
            Criar primeira tarifa
          </Button>
        </Card>
      )}

      {/* ── Tabela ── */}
      {!isLoading && !error && tariffs.length > 0 && (
        <Card variant="outlined" padding="0" className="overflow-hidden">
          <CardHeader className="px-md pt-md" action={
            <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
              {total} {total === 1 ? 'tarifa' : 'tarifas'}
            </span>
          }>
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">
              {showModalityBrand ? 'Tarifas de Cartões' : 'Ajustes Globais'}
            </h3>
          </CardHeader>
          <div className="tr-table-wrap">
            <table className="tr-table">
              <thead>
                <tr>
                  <th>{showModalityBrand ? 'Modalidade / Bandeira' : 'Serviço / Evento'}</th>
                  <th>Tipo de Cobrança</th>
                  <th className="text-right">{showModalityBrand ? 'Taxa Atual (%)' : 'Valor Atual'}</th>
                  <th className="text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {tariffs.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <div className="tr-cell-strong">
                        {showModalityBrand
                          ? [t.modality, t.brand].filter(Boolean).join(' · ') || t.service
                          : t.service}
                      </div>
                      {showModalityBrand && t.service && (
                        <div className="tr-cell-muted">{t.service}</div>
                      )}
                    </td>
                    <td>
                      <Badge variant={t.chargeType === 'PERCENTAGE' ? 'info' : 'neutral'} size="sm">
                        {CHARGE_TYPE_LABEL[t.chargeType] ?? t.chargeType}
                      </Badge>
                    </td>
                    <td className="tr-cell-money tr-cell-mono text-right tabular-nums">
                      {formatTariffValue(t)}
                    </td>
                    <td className="text-center">
                      <div className="tr-actions">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(t)}>Editar</Button>
                        <Button size="sm" variant="ghost" style={{ color: '#ba1a1a' }} onClick={() => setDeleteTarget(t)}>
                          Excluir
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ── Modal: Criar/editar ── */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar tarifa' : 'Nova tarifa'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button onClick={() => void handleSave()} loading={createMutation.isPending || updateMutation.isPending}>
              {editing ? 'Atualizar' : 'Criar'}
            </Button>
          </>
        }
      >
        <div className="tr-form">
          <Input
            label="Serviço / Evento *"
            value={form.service}
            onChange={(e) => setForm({ ...form, service: e.target.value })}
            placeholder="Ex.: TED, Manutenção, Liquidação"
          />
          {showModalityBrand && (
            <div className="tr-form__row">
              <Input
                label="Modalidade"
                value={form.modality}
                onChange={(e) => setForm({ ...form, modality: e.target.value })}
                placeholder="Débito / Crédito à vista / Parcelado"
              />
              <Input
                label="Bandeira"
                value={form.brand}
                onChange={(e) => setForm({ ...form, brand: e.target.value })}
                placeholder="VISA, MASTERCARD, ELO"
              />
            </div>
          )}
          <div className="tr-form__row">
            <Select
              label="Tipo de Cobrança"
              value={form.chargeType}
              onChange={(e) => setForm({ ...form, chargeType: e.target.value as ChargeType })}
            >
              <option value="FIXED">Fixo por Unidade</option>
              <option value="PERCENTAGE">Percentual por Venda</option>
            </Select>
            <Input
              label={form.chargeType === 'PERCENTAGE' ? 'Taxa Atual (%) *' : 'Valor Atual (R$) *'}
              type="number"
              step="0.01"
              min="0"
              value={form.currentValue}
              onChange={(e) => setForm({ ...form, currentValue: e.target.value })}
            />
          </div>
        </div>
      </Modal>

      {/* ── Modal: Confirmar exclusão ── */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Excluir tarifa"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
            <Button variant="danger" onClick={() => void handleDelete()} loading={deleteMutation.isPending}>
              Excluir
            </Button>
          </>
        }
      >
        <p style={{ fontSize: '0.875rem', color: '#43474e', margin: 0 }}>
          {deleteTarget
            ? <>Excluir a tarifa <strong>{deleteTarget.service}</strong> ({formatTariffValue(deleteTarget)})? Esta ação não pode ser desfeita.</>
            : null}
        </p>
      </Modal>

      {/* ── Toast ── */}
      {toast && (
        <div role="alert" className="tr-toast" style={{
          background: toast.variant === 'success' ? '#d3f5d6' : '#ffdad6',
          border: `1px solid ${toast.variant === 'success' ? '#b6ebbb' : '#ffb4ab'}`,
          color: toast.variant === 'success' ? '#2e7d32' : '#ba1a1a',
        }}>
          {toast.message}
          <button onClick={() => setToast(null)} aria-label="Fechar" className="tr-toast__close">&times;</button>
        </div>
      )}

      <style>{`
        .text-centered { text-align: center; }
        .muted { color: #74777f; }
        .tr-table-wrap { overflow-x: auto; }
        .tr-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .tr-table th {
          padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e;
          font-weight: 600; font-size: 0.75rem; text-transform: uppercase;
          letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5;
          text-align: left; white-space: nowrap; position: sticky; top: 0; z-index: 1;
        }
        .tr-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; color: #191c1e; }
        .tr-table tbody tr:hover td { background: #eceef0; }
        .tr-cell-strong { font-weight: 600; }
        .tr-cell-muted { color: #43474e; font-size: 0.8125rem; }
        .tr-cell-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 0.8125rem; }
        .tr-cell-money { font-weight: 600; color: #002045; }
        .tr-actions { display: flex; gap: 0.25rem; justify-content: center; flex-wrap: wrap; }
        .tr-form { display: flex; flex-direction: column; gap: 1rem; min-width: 420px; }
        .tr-form__row { display: flex; gap: 0.75rem; }
        .tr-form__row > * { flex: 1; }
        .tr-toast {
          position: fixed; bottom: 1.5rem; right: 1.5rem; padding: 0.75rem 1rem;
          border-radius: 8px; font-size: 0.875rem; font-weight: 500;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 10000; max-width: 400px;
        }
        .tr-toast__close { margin-left: 0.75rem; background: none; border: none; cursor: pointer; color: inherit; font-size: 1rem; padding: 0; line-height: 1; }
      `}</style>
    </div>
  );
}